import type { Language } from '@eformulieren/engine'
import { type ReactNode, useCallback, useState } from 'react'
import type { Bilingual } from '../../language/text.js'

/**
 * Een teken dat niet kan worden verstuurd, weggelaten bij de invoer (koppelvlak hoofdstuk 3).
 *
 * Het bericht aan het processysteem is XML, en XML 1.0 laat de meeste stuurtekens, U+FFFE, U+FFFF en
 * een losse surrogaathelft niet toe (`xmlCharacters.ts` in het model). Ze komen bijna altijd mee met
 * geplakte tekst en zijn op het scherm onzichtbaar. De tekstvelden en rich text laten ze daarom bij
 * plakken, slepen en typen weg, en zeggen dat onder het veld: wie iets plakt en een teken kwijt is,
 * hoort waarom. De harde controle in de engine (`characters.ts`) is het slot erachter.
 *
 * De melding staat in een live region die er altijd is, zodat een schermlezer hem hoort op het
 * moment dat hij verschijnt (R-139); zonder inhoud neemt hij geen ruimte in.
 */

export const REFUSED_CHARACTERS: Bilingual = {
  nl: 'Een onzichtbaar teken uit wat u invoerde is weggelaten, want het kan niet worden verstuurd. De rest van de tekst staat er.',
  en: 'An invisible character in what you entered has been left out, because it cannot be sent. The rest of the text is there.',
}

/**
 * Of de melding nu staat, en de functie die de binding na elke eigen invoer aanroept: met `true` als
 * er iets is weggelaten, met `false` als de invoer schoon was. Een schone invoer daarna haalt hem weg.
 */
export function useRefusedCharacters(): { readonly refused: boolean; readonly report: (removed: boolean) => void } {
  const [refused, setRefused] = useState(false)
  const report = useCallback((removed: boolean) => setRefused(removed), [])
  return { refused, report }
}

export function RefusedCharactersNotice({ refused, taal: language }: { readonly refused: boolean; readonly taal: Language }): ReactNode {
  return (
    <div className="ef-veld__tekens" role="status">
      {refused ? <p className="ef-veld__melding ef-veld__melding--zacht">{REFUSED_CHARACTERS[language]}</p> : null}
    </div>
  )
}
